/** User-specific navigation shortcuts. These are opened workspaces, not audit events. */
export interface RecentWorkItem {
  href: string;
  title: string;
  machineId: string;
  machineName: string;
}

export const RECENT_WORK_LIMIT = 5;
const KEY_PREFIX = 'route42:recent-work:v1';

export function recentWorkStorageKey(customerId: string, userId: string): string {
  return `${KEY_PREFIX}:${encodeURIComponent(customerId)}:${encodeURIComponent(userId)}`;
}

export function recentWorkDestination(pathname: string): { machineId: string; title: string } | null {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== 'machines' || !parts[1] || parts[1] === 'new' || parts.length > 4) return null;
  const machineId = parts[1];
  if (parts.length === 2) return { machineId, title: 'Machine' };
  const section = parts[2];
  if (section === 'memory' && parts.length === 3) return { machineId, title: 'Machine Memory' };
  if (section === 'activity' && parts.length === 3) return { machineId, title: 'Activity' };
  if (section === 'conversations') {
    if (parts[3] === 'new') return null;
    return { machineId, title: parts.length === 4 ? 'Conversation' : 'Conversations' };
  }
  if (section === 'investigations') return { machineId, title: parts.length === 4 ? 'Investigation' : 'Investigations' };
  if (section === 'documents') {
    if (parts[3] === 'add') return null;
    return { machineId, title: parts.length === 4 ? 'Document' : 'Documents' };
  }
  return null;
}

export function readRecentWork(storage: Pick<Storage, 'getItem'>, key: string): RecentWorkItem[] {
  try {
    const raw = storage.getItem(key);
    const value: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is RecentWorkItem => {
      if (!item || typeof item !== 'object') return false;
      const candidate = item as Partial<RecentWorkItem>;
      const route = typeof candidate.href === 'string' ? recentWorkDestination(candidate.href) : null;
      return Boolean(route && candidate.machineId === route.machineId && typeof candidate.title === 'string' && candidate.title.length <= 120 && typeof candidate.machineName === 'string' && candidate.machineName.length <= 120);
    }).slice(0, RECENT_WORK_LIMIT);
  } catch {
    return [];
  }
}

export function addRecentWork(items: RecentWorkItem[], next: RecentWorkItem): RecentWorkItem[] {
  return [next, ...items.filter((item) => item.href !== next.href)].slice(0, RECENT_WORK_LIMIT);
}
